import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import DecimalField from '../shared/DecimalField';

/**
 * Omavastuu (deductible) for a NON-insurance billing customer.
 *
 * In insurance mode the deductible lives inside the "Vakuutustiedot" dialog; this small
 * dialog exposes the same Maintenance.deductible_amount field when the billing customer
 * is e.g. an employer or a leasing company. Explicit Save/Cancel, no save-on-blur.
 */
function DeductibleDialog({ open, handleClose, maintenance, setMaintenance, onError }) {
  const { t } = useTranslation();
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setAmount(maintenance?.deductible_amount ?? '');
    }
  }, [open, maintenance?.deductible_amount]);

  const hasSaved = parseFloat(maintenance?.deductible_amount) > 0;
  const parsed = parseFloat(amount);
  const isValid = amount === '' || (!Number.isNaN(parsed) && parsed >= 0);

  const save = async (value) => {
    setSaving(true);
    try {
      const res = await axios.patch(`/api/maintenances/${maintenance.id}/update_insurance_details/`, {
        deductible_amount: value,
      });
      setMaintenance((prev) => ({ ...prev, ...res.data, _original_deductible_amount: res.data.deductible_amount }));
      handleClose();
    } catch {
      if (onError) onError();
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => save(amount === '' || parseFloat(amount) === 0 ? null : amount);
  const handleRemove = () => save(null);

  return (
    <Dialog open={open} onClose={saving ? undefined : handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('Deductible')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('deductibleNonInsuranceHelp')}
        </Typography>
        {maintenance?.billing_customer && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">{t('Billing customer')}:</Typography>
            <Typography variant="body2" fontWeight={600}>{maintenance.billing_customer.name}</Typography>
          </Box>
        )}
        <DecimalField
          label={t('Deductible amount') + ' (€)'}
          value={amount}
          onChange={setAmount}
          size="small"
          fullWidth
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter' && isValid && !saving) {
              e.preventDefault();
              handleSave();
            }
          }}
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        <Box>
          {hasSaved && (
            <Button color="error" onClick={handleRemove} disabled={saving}>
              {t('Remove deductible')}
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={handleClose} disabled={saving}>{t('Cancel')}</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !isValid}>
            {t('Save')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default DeductibleDialog;
